import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Card, Form, Badge, Button } from 'react-bootstrap';
import { FaBolt, FaQuestionCircle, FaBolt as FaAction, FaTimes, FaPlus } from 'react-icons/fa';

// --- Trigger Node ---
export const TriggerNode = memo(({ data, isConnectable }: NodeProps) => {
    // data: { keywords: string[], onChange: (keywords: string[]) => void }

    const [input, setInput] = React.useState('');

    const addKeyword = () => {
        if (input.trim()) {
            const newKeywords = [...(data.keywords || []), input.trim()];
            data.onChange(newKeywords);
            setInput('');
        }
    };

    const removeKeyword = (index: number) => {
        const newKeywords = (data.keywords || []).filter((_: any, i: number) => i !== index);
        data.onChange(newKeywords);
    };

    return (
        <div className="flow-node flow-node-trigger">
            <Handle type="source" position={Position.Bottom} isConnectable={isConnectable} className="flow-node-handle flow-handle-trigger" />
            <Card className="flow-node-card shadow border-0">
                <Card.Header className="flow-node-header flow-node-header-trigger py-2 d-flex align-items-center">
                    <FaBolt className="me-2" />
                    <span className="fw-bold">Trigger (Start)</span>
                </Card.Header>
                <Card.Body className="p-3">
                    <Form.Text className="text-muted d-block mb-2">Flow starts when user says:</Form.Text>
                    <div className="d-flex mb-2">
                        <Form.Control
                            size="sm"
                            placeholder="Add keyword..."
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && addKeyword()}
                        />
                        <Button size="sm" variant="outline-warning" onClick={addKeyword}><FaPlus /></Button>
                    </div>
                    <div className="d-flex flex-wrap gap-1">
                        {(data.keywords || []).map((kw: string, i: number) => (
                            <Badge key={i} bg="light" text="dark" className="border">
                                {kw} <FaTimes className="cursor-pointer ms-1" onClick={() => removeKeyword(i)} />
                            </Badge>
                        ))}
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
});

// --- Step Node ---
export const StepNode = memo(({ data, isConnectable }: NodeProps) => {
    // data: { question: string, variable: string (auto from question), onChange: (field: string, val: string) => void }

    return (
        <div className="flow-node flow-node-step">
            <Handle type="target" position={Position.Top} isConnectable={isConnectable} className="flow-node-handle flow-handle-step flow-handle-target" />
            <Card className="flow-node-card shadow border-0">
                <Card.Header className="flow-node-header flow-node-header-step text-white py-2 d-flex align-items-center">
                    <FaQuestionCircle className="me-2" />
                    <span className="fw-bold">Step (Question)</span>
                </Card.Header>
                <Card.Body className="p-3">
                    <Form.Group className="mb-0">
                        <Form.Label className="small text-muted mb-0">Bot Asks:</Form.Label>
                        <Form.Control
                            size="sm"
                            as="textarea"
                            rows={2}
                            value={data.question}
                            onChange={e => data.onChange('question', e.target.value)}
                            placeholder="e.g. What is your email?"
                        />
                    </Form.Group>
                </Card.Body>
            </Card>
            <Handle type="source" position={Position.Bottom} isConnectable={isConnectable} className="flow-node-handle flow-handle-step flow-handle-source" />
        </div>
    );
});

// --- Action Node ---
export const ActionNode = memo(({ data, isConnectable }: NodeProps) => {
    // data: { actions, completion_message_success, completion_message_failure, onChange, onAddAction, onRemoveAction, onUpdateAction }

    return (
        <div className="flow-node flow-node-action">
            <Handle type="target" position={Position.Top} isConnectable={isConnectable} className="flow-node-handle flow-handle-action flow-handle-target" />
            <Card className="flow-node-card shadow border-0">
                <Card.Header className="flow-node-header flow-node-header-action text-white py-2 d-flex align-items-center justify-content-between">
                    <div>
                        <FaAction className="me-2" />
                        <span className="fw-bold">End & Actions</span>
                    </div>
                    <Button size="sm" variant="light" className="py-0 px-2 text-success small" onClick={data.onAddAction}><FaPlus /> Action</Button>
                </Card.Header>
            <Card.Body className="p-3">

                {/* Actions List */}
                <div className="mb-3">
                    <Form.Label className="small text-muted fw-bold">Actions Executed:</Form.Label>
                    {(data.actions || []).length === 0 && <div className="text-muted small fst-italic">No extra actions (just completion)</div>}

                    {(data.actions || []).map((action: any, idx: number) => (
                        <div key={idx} className="bg-light p-2 rounded mb-2 border border-success-subtle">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <Badge bg="success" className="text-white">{action.type.toUpperCase()}</Badge>
                                <FaTimes className="text-danger cursor-pointer" onClick={() => data.onRemoveAction(idx)} />
                            </div>
                            {action.type === 'email' && (
                                <>
                                    <Form.Control
                                        size="sm"
                                        className="mb-1"
                                        placeholder="To Email"
                                        value={action.recipient_email || ''}
                                        onChange={e => data.onUpdateAction(idx, 'recipient_email', e.target.value)}
                                    />
                                    <Form.Control
                                        size="sm"
                                        placeholder="Subject"
                                        value={action.subject || ''}
                                        onChange={e => data.onUpdateAction(idx, 'subject', e.target.value)}
                                    />
                                </>
                            )}
                        </div>
                    ))}
                </div>

                <hr className="my-2" />

                <Form.Group className="mb-2">
                    <Form.Label className="small text-muted fw-bold">Completion — Success (True):</Form.Label>
                    <Form.Control
                        size="sm"
                        as="textarea"
                        rows={2}
                        value={data.completion_message_success ?? ''}
                        onChange={e => data.onChange('completion_message_success', e.target.value)}
                        placeholder="Message when actions succeed..."
                    />
                </Form.Group>
                <Form.Group>
                    <Form.Label className="small text-muted fw-bold">Completion — Failure (False):</Form.Label>
                    <Form.Control
                        size="sm"
                        as="textarea"
                        rows={2}
                        value={data.completion_message_failure ?? ''}
                        onChange={e => data.onChange('completion_message_failure', e.target.value)}
                        placeholder="Message when an action fails..."
                    />
                </Form.Group>
            </Card.Body>
        </Card>
        </div>
    );
});
